import {
  COMPOSE_ID,
  OWN_ID,
  deskSummary,
  isHeldSpot,
  knockRefOf,
  type Conversation,
} from "../../lib/conversations";
import { useSessionExpired } from "../../lib/session";
import { BackIcon, RailIcon, ShareIcon } from "../ui/icons";
import { Button, LinkButton, QuietButton } from "../ui/button";
import { XHandleLink } from "../ui/x-link";
import { OpenedDoorsShelf } from "../doorman/door-opened";
import { DoormanArea } from "../doorman/doorman-area";
import { ActionStatus, KnockCard } from "../inbox/knock-card";
import { KnockChat } from "../inbox/knock-chat";
import { KnockStartedScreen } from "../knock/started";
import { SpotThread } from "../waiting/spot-thread";
import { ComposePane } from "./compose-pane";
import { ConversationMenu } from "./conversation-menu";
import { DoorChip } from "./chip";
import { ConversationFace, NobFace } from "./mascot";
import { isDesktop, useShellUi } from "./state";
import { useMetered } from "./use-metered";
import { PERSONALIZE_ASK } from "./right-rail";
import type { InboxDesk } from "./desk";
import type { ShellWorld } from "./use-conversations";

export const PICK_A_DOOR = "Pick a door";
export const PICK_A_DOOR_LINE =
  "Your conversations are on the left. Knock, or wait for someone to make their case.";
export const READING_KNOCK = "Reading the knock.";
export const KNOCK_UNREACHABLE = "Nob is not answering. Try that again.";
/**
 * The knock is GONE, not unreachable (b gap 4, wave 17b).
 *
 * A block takes the conversation out of everything the server answers, and the
 * knock read 404s from that instant. The pane used to draw "Nob is not
 * answering. Try that again." over it, with a retry that could only ever fetch
 * the same 404 — which is the product blaming itself for doing what it was
 * asked. One quiet sentence, and no way to press it: the row leaves the lists
 * on its own when the undo window shuts.
 */
export const KNOCK_GONE = "This conversation is not at your door any more.";
export const OPEN_THE_DESK = "Open the desk";
export const TUNE_DOOR_TITLE = "Your door has the starter rules";
export const TUNE_DOOR_BODY =
  "Three questions and your Nob knows what to ask people. You can change every answer afterwards.";
export const TUNE_DOOR_ACTION = "Set your rules";

/**
 * The middle pane: one conversation, whichever one is open.
 *
 * It draws the header and nothing else. Everything below the header is a
 * surface that already existed and is handed the pane's height — the doorman
 * conversation, a knock standing at your door, a hearing you are sitting in.
 * That is deliberate: this wave moved the furniture, and a rewrite of what is
 * inside the rooms while the walls were being moved would be two changes
 * neither of which could be reviewed.
 */
export function ChatPane({ world, desk }: { world: ShellWorld; desk: InboxDesk }) {
  const ui = useShellUi();
  const conv = ui.activeId ? world.byId(ui.activeId) : undefined;

  if (!conv) {
    return (
      <section data-pane="chat" className="grid h-full min-h-0 place-items-center bg-bg px-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <NobFace mood="bored" size="desk" />
          <p className="nob-empty-title mt-2">{PICK_A_DOOR}</p>
          <p className="nob-empty-line">{PICK_A_DOOR_LINE}</p>
        </div>
      </section>
    );
  }

  return (
    <section data-pane="chat" className="flex h-full min-h-0 flex-col bg-bg">
      <Header conv={conv} world={world} desk={desk} />
      <div className="min-h-0 flex-1">
        <Body conv={conv} world={world} desk={desk} />
      </div>
    </section>
  );
}

function Header({ conv, world, desk }: { conv: Conversation; world: ShellWorld; desk: InboxDesk }) {
  const ui = useShellUi();
  const own = conv.kind === "own-nob";
  const summary = own && world.hasDoor ? deskSummary(world.conversations) : null;
  /**
   * A knock at the reader's own door, on a phone (wave 83): the one header the
   * first UI review found truncating. The knocker's side is left as it was.
   */
  const phoneIncoming = !isDesktop() && !own && conv.direction === "incoming";
  /**
   * BLOCK, HANDED TO THE MENU RATHER THAN RE-IMPLEMENTED IN IT (§3).
   *
   * `desk` already holds the shell's one copy of the five verbs, with the undo
   * window and the deferred list re-read that make Block survivable. The menu
   * gets the two ends of it — confirm, and undo — plus enough state to draw the
   * promise; everything else about the act stays in `useInboxActions`, so there
   * is one write and one window however the owner reached the verb.
   *
   * Absent unless the pane has resolved a knock at this reader's own door: the
   * verb has nothing to act on otherwise, and a menu item that cannot work is
   * worse than one that is not there.
   */
  const block = desk.knock
    ? {
        undoable: desk.cardUi.pending?.verb === "block",
        note: desk.cardUi.note,
        busy: desk.busy,
        // `onConfirmBlock`, not `onBlock`: the latter opens the CARD's inline
        // confirm, which on a through thread is folded away under the
        // conversation. The menu asks its own question and then acts.
        onConfirm: desk.handlers.onConfirmBlock,
        onUndo: desk.handlers.onUndo,
      }
    : undefined;
  return (
    // `min-h-14`, not `h-14`: a name that needs two lines on a phone gets them
    // (wave 82) rather than a header that clips it.
    <header className="nob-header flex min-h-14 shrink-0 items-center gap-2 border-b border-border px-3 py-1">
      <button
        type="button"
        aria-label="Back to your conversations"
        onClick={() => ui.setMobilePane("list")}
        className="nob-press grid size-9 shrink-0 place-items-center rounded-full text-muted
                   hover:bg-hover hover:text-text focus-visible:outline-none focus-visible:ring-2
                   focus-visible:ring-focus/70 md:hidden"
      >
        <BackIcon className="h-5 w-5" />
      </button>
      <ConversationFace conv={conv} doorLive={world.doorLive} />
      <div className="min-w-0 flex-1">
        <p className="flex min-w-0 items-center gap-2">
          {/* WRAPS TO TWO LINES BEFORE IT TRUNCATES (wave 82). At 375 the name
              was cut to "Cardi…" beside the chip, and whose door this is is the
              one thing this header is for. */}
          <span className="line-clamp-2 min-w-0 text-[15px] font-semibold leading-tight tracking-tight [overflow-wrap:anywhere]">
            {conv.name}
          </span>
          {/* A door that has not been hung is neither live nor paused, and the
              header must not pick one of them. Same rule the pinned row keeps.

              And a "Paused" here is the WAY BACK UP (owner item 13): this is the
              most-looked-at printing of the word in the product, and it used to
              be a readout with its remedy five sections down a settings page. */}
          {/* ON A PHONE THE NAME GETS THE LINE (wave 83): at 375 the chip took
              the width the name needed, and the name read "Profbrnf…". On a
              knock at the reader's door the chip moves down beside the handle,
              in its short form, which carries the long one as its title.
              Everywhere else it stays beside the name and is the short word
              below `sm` (wave 82) — "Sent" rather than "Sent to Cardiofpw2gv". */}
          {(!own || world.hasDoor) && !phoneIncoming && (
            <DoorChip conv={conv} doorLive={world.doorLive} reopenable={own} responsive />
          )}
        </p>
        {/* FR-6.4 — the owner's own thread carries a one-line state of the
            door, in the slot the handle occupies on every other row. It is the
            only row whose handle is already all over the rest of the screen,
            and the only row a summary is about. A door with nothing standing at
            it falls back to the handle rather than to a sentence about zero. */}
        {own && summary ? (
          <p className="truncate text-xs text-faint">{summary}</p>
        ) : (
          // The handle goes to the profile behind it (owner, 2026-08-25). This
          // header is the one line that says WHO is on the other side of the
          // conversation, and a recipient deciding whether to answer wants to
          // look them up from exactly here.
          // …unless the name IS the handle, which is every row about somebody
          // who has not signed up (a spot the reader holds) and every sender
          // with no display name. Printing it twice is not a second fact.
          <>
            {phoneIncoming ? (
              <p className="flex min-w-0 items-center gap-2 text-xs text-faint">
                <DoorChip conv={conv} doorLive={world.doorLive} compact />
                {conv.handle && conv.name !== `@${conv.handle}` && (
                  <span className="truncate">
                    <XHandleLink handle={conv.handle} />
                  </span>
                )}
              </p>
            ) : (
              conv.handle &&
              conv.name !== `@${conv.handle}` && (
                <p className="truncate text-xs text-faint">
                  <XHandleLink handle={conv.handle} />
                </p>
              )
            )}
          </>
        )}
      </div>
      {/* Handing out your door only makes sense on your own door. */}
      {own && world.hasDoor && (
        <button
          type="button"
          aria-label="Hand out your door"
          onClick={() => ui.openSheet("share")}
          className="nob-press grid size-9 shrink-0 place-items-center rounded-full text-muted
                     hover:bg-hover hover:text-text focus-visible:outline-none focus-visible:ring-2
                     focus-visible:ring-focus/70"
        >
          <ShareIcon className="h-4 w-4" />
        </button>
      )}
      {/* Archive and Delete (R-0828-1). Header, not card: the card is the
          incoming surface and it folds away once a knock is through, and these
          two act on every conversation in either direction. It renders nothing
          for the owner's own thread, a held spot or an unsent draft — none of
          them is a knock there is a copy of. */}
      <ConversationMenu conv={conv} block={block} />
      <button
        type="button"
        aria-label="Toggle the desk"
        aria-pressed={ui.rightOpen}
        onClick={ui.toggleRail}
        className={`nob-press grid size-9 shrink-0 place-items-center rounded-full hover:bg-hover
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus/70 ${
                      ui.rightOpen ? "bg-selected text-text" : "text-muted"
                    }`}
      >
        <RailIcon className="h-4 w-4" />
      </button>
    </header>
  );
}

function Body({ conv, world, desk }: { conv: Conversation; world: ShellWorld; desk: InboxDesk }) {
  const ui = useShellUi();
  /**
   * ALL THREE ANSWERS (`use-metered.ts`): `null` is "nobody has said yet", and
   * treating it as Free sent a subscriber pressing the shelf's CTA on a cold
   * load to the rules editor rather than to their doorman. The row waits — see
   * the `onEditRules` prop below, where `null` disables it.
   */
  const metered = useMetered();
  const sessionExpired = useSessionExpired();

  /**
   * A door this person was waiting at has opened (owner item 3, `door-opened.tsx`).
   *
   * THE SAME ACT FROM THREE SURFACES, so it is written once here: the doorman
   * thread's shelf, and the two doorless panes below that stand in for it. What
   * it does is the console's ordinary knock — a compose thread at that door,
   * carrying the words they wrote when they queued as its opening draft, still
   * theirs to change and still nobody's to send but theirs.
   */
  const knockAtOpened = (handle: string, line: string) => ui.openCompose(handle, line);

  /**
   * A knocker's own row, and there is no doorman conversation behind it: the
   * thread is a door's, and this session has not hung one.
   *
   * ONE REASON TO BE HERE, now that there is only one way in (ruling R-0826-1):
   * somebody who signed in with X and stepped aside from the door step (§2.3.3)
   * owes one screen, and this offers it — the same act the pinned row offers,
   * from the shell's one copy of it. The second reason, an email knocker who
   * could never own a door, is void: every account arrives holding an X
   * identity.
   *
   * IT CARRIES THE OPENED-DOOR SHELF. Somebody with no door of their own is
   * precisely the registrant this product must not lose track of — they came to
   * reach one person and queued for it — and this pane has no masthead for the
   * news to land in.
   */
  if (conv.id === OWN_ID && world.owesDoorStep && ui.setUpDoor) {
    // Above the invitation rather than instead of it: this person owes a door
    // step AND has somewhere to knock, and neither cancels the other.
    return (
      <div className="flex h-full min-h-0 flex-col">
        <OpenedDoorsShelf onKnockAt={knockAtOpened} />
        <div className="min-h-0 flex-1">
          <DoorStepInvitation onSetUp={ui.setUpDoor} />
        </div>
      </div>
    );
  }

  if (conv.id === OWN_ID) {
    return (
      <DoormanArea
        key="doorman"
        embedded
        prefill={ui.pendingAsk}
        onSeeAll={() => {
          ui.setFilter("passed");
          ui.setMobilePane("list");
        }}
        // The briefing's one verb (owner, 2026-08-26): a recap card opens the
        // knock's own conversation, which is where every verb already lives.
        // The shell's existing navigation, nothing new — `setActive` is what a
        // row in the list does, and this is the same act from a card.
        // The knock sits inside a thread: the card names the knock, the list is
        // keyed by the person, and the world knows which is which.
        onOpenKnock={(knockId) => {
          const thread = world.threadOfKnock(knockId);
          if (thread) ui.setActive(thread.id);
        }}
        // The same two acts the desk offers, from the shell's one copy of
        // each: handing the door out is the sheet, and personalising the rules
        // is a conversation with a doorman that can have one — or, on Free,
        // the editor, because that doorman cannot (ruling C10). The Free road
        // lands ON the rules controls, not at the top of the page they are five
        // blocks down (owner item 8) — the same `"rules"` intent the desk's own
        // copy of this button carries, because it is the same act.
        onShareDoor={() => ui.openSheet("share")}
        // `null` is not a road: while the tier is unknown the row is disabled
        // rather than pointed at Free's editor.
        onEditRules={
          metered === null
            ? null
            : () =>
                metered ? ui.askDoorman(PERSONALIZE_ASK, true) : ui.openSettings("door", "rules")
        }
        // R-0825-4: a Free owner's composer is a locked bar, and this is where
        // it goes. The plans view and not the panel index: what Free lacks is a plan.
        onSeePlans={() => ui.openBilling("plans", "options")}
        onStartTrial={() => ui.openBilling("plans", "trial")}
        // Owner (2026-08-27): a paused door announces itself where the owner
        // already is — a standing CTA in this thread's shelf — and pressing it
        // opens the shell's one reopen confirm, same as every printed "Paused".
        doorPaused={world.doorPaused}
        onReopenDoor={() => ui.openSheet("reopen")}
        // A door this person was waiting at has opened, and their own line is
        // the draft (owner item 3). The shelf is drawn by the thread; where the
        // knock GOES is the shell's, like every other verb on this pane.
        onKnockAt={knockAtOpened}
        onSessionExpired={sessionExpired}
      />
    );
  }

  /**
   * A held spot (PRD FR-6.3, WR FR-G9.3). NO COMPOSER, and the screen says why.
   *
   * ABOVE THE COMPOSE BRANCH, which is not an ordering detail: a compose thread
   * opened at a handle with no door BECOMES this the moment interest is
   * registered there, wearing the same id so nothing is selected out from under
   * the reader (`use-conversations.ts`). Asking the stage first is what lets the
   * pane follow. It is also above `knockRefOf`, which would return null for a
   * spot's id and drop the pane to blank.
   *
   * TWO SPOTS, TWO PANES, and the direction is the whole of the difference:
   * incoming is somebody waiting at YOUR handle from before you opened, and
   * there is nothing to do about it but read it; outgoing is a spot of your
   * OWN, which is a line you wrote and can rewrite.
   */
  if (isHeldSpot(conv)) {
    if (conv.direction === "outgoing") {
      // No way to close itself: stopping keeps the conversation (F-27) — the
      // thread reads as stopped where it stands, and offers the spot again.
      return <SpotThread key={conv.id} conv={conv} />;
    }
    return <HeldSpotPane conv={conv} onOpenDesk={() => ui.setRightOpen(true)} />;
  }

  /**
   * A knock being written, and then the hearing it becomes (owner, 2026-08-26).
   *
   * KEYED ON THE ID, NOT ON THE STAGE, and that is the whole seam: before the
   * send the row is synthetic and staged `composing`; after it the row is the
   * server's own outgoing knock wearing this thread's id, so its stage is
   * whatever the server says. The pane is the same pane either way, because it
   * is the same conversation — which is the point of moving the knock in here.
   *
   * …UNLESS THERE IS ALREADY A CONVERSATION AT THIS DOOR (§2, §6.4). One knock
   * exists per sender and door, so opening a compose thread at a door already
   * knocked on opens that conversation and never a second knock: the row
   * carries a knock, so the branch below draws the hearing and the plate is not
   * offered. `ui.compose.knockId` is what keeps the two apart — once this
   * console has STARTED a knock the pane stays here, because the pane is what
   * is holding that stream and unmounting it mid-answer would lose it.
   *
   * Above `knockRefOf` for the reason the held spot is: an unsent compose names
   * no knock, so the resolver answers null and the pane would drop to blank.
   */
  if (ui.compose && conv.id === COMPOSE_ID && (ui.compose.knockId || !knockRefOf(conv))) {
    return <ComposePane compose={ui.compose} world={world} />;
  }

  /**
   * A THREAD IS ONE KNOCK PER DIRECTION, AND THE PANE DRAWS THE ONE THE ROW
   * WEARS (`docs/conversation-model.md` §6.3). There is no history above it and
   * no divider through it: the follow-up mechanism that made chapters is
   * deleted, so what is on screen IS the conversation, top to bottom. The
   * composer is that knock's — answer, or talk to your Nob, or talk to theirs.
   */
  const ref = knockRefOf(conv);
  if (!ref) return null;

  if (ref.side === "out") {
    return (
      <div className="h-full min-h-0 px-2 sm:px-4">
        <KnockStartedScreen
          key={conv.id}
          embedded
          knockId={ref.knockId}
          handle={conv.handle}
          doorName={conv.name}
          doorAvatar={conv.avatar}
          opening={null}
          turnsUsed={null}
          maxTurns={null}
          onSessionExpired={sessionExpired}
          onGone={() => ui.setActive(null)}
          onOpenPlans={() => ui.openBilling("plans", "options")}
          onStartTrial={() => ui.openBilling("plans", "trial")}
          onLeave={() => ui.setMobilePane("list")}
        />
      </div>
    );
  }

  return (
    <AtTheDoor
      conv={conv}
      doorLive={world.doorLive}
      knockId={ref.knockId}
      desk={desk}
      onOpenDesk={() => ui.setRightOpen(true)}
    />
  );
}

/**
 * What a held spot opens into: their line, and the reason there is no box to
 * type in. The panel is required to state that it becomes a knock when they
 * write (FR-G9.5), and no verb on this screen can send anything.
 */
function HeldSpotPane({ conv, onOpenDesk }: { conv: Conversation; onOpenDesk: () => void }) {
  return (
    <div className="grid h-full min-h-0 place-items-center px-6">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <NobFace mood="watching" size="desk" />
        <p className="nob-empty-title mt-2">{HELD_TITLE}</p>
        <p className="text-sm leading-snug text-text-soft">{conv.preview}</p>
        <p className="text-sm leading-snug text-muted">{HELD_BODY}</p>
        <div className="mt-2 md:hidden">
          <QuietButton type="button" onClick={onOpenDesk}>
            Open the desk
          </QuietButton>
        </div>
      </div>
    </div>
  );
}

export const HELD_TITLE = "Holding a spot";
export const HELD_BODY =
  "They registered interest before your door opened. There is nothing to reply to yet — " +
  "when they knock, this becomes a knock and your Nob hears them out.";

/**
 * The Rules step, offered where a knocker went looking for their desk (§2.3.2 #4;
 * the door itself is already live, ruling R-1006-2).
 * One screen, and it says so — the funnel argument in §2.3.3 is that the ask is
 * small and the moment is theirs to choose.
 */
function DoorStepInvitation({ onSetUp }: { onSetUp: () => void }) {
  return (
    <div className="grid h-full min-h-0 place-items-center px-6">
      <div className="flex max-w-sm flex-col items-center gap-3 text-center">
        <NobFace mood="watching" size="desk" />
        <p className="nob-empty-title mt-2">{TUNE_DOOR_TITLE}</p>
        <p className="text-sm leading-snug text-muted">{TUNE_DOOR_BODY}</p>
        <div className="mt-2">
          <Button type="button" onClick={onSetUp}>
            {TUNE_DOOR_ACTION}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * A knock standing at your door — A CHAT WINDOW, since ruling 2026-08-25 #9.
 *
 * It was the card and the five verbs, with the grilling folded away above them.
 * It is now the whole conversation about this person, in one column with three
 * parties in it: the stranger, the owner's Nob, and the owner, who can type.
 * The card is still here and still carries the standing and the verbs — it
 * simply sits at the head of the thread rather than being the whole of it, and
 * the transcript below it is the conversation rather than a disclosure widget.
 */
function AtTheDoor({
  conv,
  doorLive,
  knockId,
  desk,
  onOpenDesk,
}: {
  conv: Conversation;
  doorLive: boolean;
  knockId: string;
  desk: InboxDesk;
  onOpenDesk: () => void;
}) {
  if (desk.gone) {
    return <p className="px-6 py-10 text-sm text-faint">{KNOCK_GONE}</p>;
  }

  if (desk.failed) {
    return (
      <div className="px-6 py-10">
        <p className="text-sm text-faint" role="alert">
          {KNOCK_UNREACHABLE}
        </p>
        <p className="mt-4">
          <LinkButton type="button" onClick={desk.reload}>
            Try again
          </LinkButton>
        </p>
      </div>
    );
  }

  if (!desk.knock) {
    return <p className="nob-breathe px-6 py-10 text-sm text-faint">{READING_KNOCK}</p>;
  }

  return (
    <KnockChat
      key={knockId}
      conv={conv}
      doorLive={doorLive}
      knockId={knockId}
      transcript={desk.transcript}
      attachments={desk.knock.attachments}
      onSettled={desk.reload}
      /* The card's own door verbs, so the answer that says "only you can move
         this gate" can carry the button that moves it (R-0910-13 rule 3). Same
         handlers as the row above: one action, offered where it was asked for. */
      verbs={{
        onOpenDoor: desk.handlers.onReply,
        onNotForMe: desk.handlers.onNotInterested,
        onBlock: desk.handlers.onBlock,
      }}
      /* The confirmation and its Undo, under the composer rather than on the
         card — the card folds away the instant a reply opens the door, and it
         used to take them with it (F-58). */
      status={<ActionStatus ui={desk.cardUi} onUndo={desk.handlers.onUndo} />}
      above={
        // The card is an `<li>`: it was a row in a list, and it still is one —
        // a list of exactly one, which is what this pane holds. A list marker
        // would be a bullet beside a knock, so the list carries none.
        <ul className="list-none">
          <KnockCard knock={desk.knock} ui={desk.cardUi} handlers={desk.handlers} />
          {/* The desk carries the same verdict and the same verbs; on a phone it
              is a layer over this rather than a column beside it, and this is
              how it opens. */}
          <li className="mt-4 text-center md:hidden">
            <LinkButton type="button" onClick={onOpenDesk}>
              {OPEN_THE_DESK}
            </LinkButton>
          </li>
        </ul>
      }
    />
  );
}
